/**
 * @coeldery/member-card —— **數碼會員卡**（版面元件）
 *
 * 為何係模組（R1／R3）：任何會員制 app 都要「顯示會員身分」嘅卡（姓名／編號／種類／有效期）。
 *   查庫證據（2026-10-05）：`--search member｜card` 只有 `member-header`（家庭樹身份區）、
 *   `household-card`（家庭卡）、`merchant-preview-card`（商戶）→ 冇通用會員卡 → 依 R3 新建。
 *
 * ⚠️ 全部資料由 props 傳入（**唔內置 mock**）；顏色一律 CSS var；文字一律由 props 入（唔 `t()`）。
 */
export interface MemberCardProps {
  /** 協會／品牌名（例 HKSCA）；**冇 `logoUrl` 時**用呢個做左上角文字 */
  brandLabel: string;
  /** ⭐ 左上角 logo（可選；例 `/brand-mark.png`）。提供咗就代替 `brandLabel` 文字 */
  logoUrl?: string | null;
  /** ⭐ 右上角相片（可選；data URL 或網址）。未提供 → 唔顯示（狀態 pill 會留在姓名旁） */
  photoUrl?: string | null;
  name: string;
  /** 會員編號（未派 → null） */
  memberNo?: string | null;
  /** 會籍種類顯示名（由呼叫者本地化） */
  typeLabel: string;
  statusLabel: string;
  company?: string | null;
  /** 有效期（已本地化字串；null／未設 → 顯示 `—`） */
  expiresText?: string | null;
  /** label */
  memberNoLabel: string;
  expiresLabel: string;
}

export default function MemberCard({
  brandLabel,
  logoUrl,
  photoUrl,
  name,
  memberNo,
  typeLabel,
  statusLabel,
  company,
  expiresText,
  memberNoLabel,
  expiresLabel,
}: MemberCardProps) {
  return (
    <article
      style={{
        /*
         * 漸變：**頂帶近白**（畀 logo 直接放，唔會見到白底方塊）→ 品牌深色 → 品牌漸變紫。
         * ⚠️ 用 **px 停止點**（唔用 %）—— 咁樣「白帶高度」同 layout 對得上：
         *    上內距 20 ＋ logo 34 ＝ 54px，所以白帶到 56px、74px 前完全入品牌色，
         *    令下面嘅文字（姓名／種類…）全部落喺深色區，白色字有足夠對比。
         */
        background:
          'linear-gradient(180deg, var(--color-card-top, #ffffff) 0, var(--color-card-top, #ffffff) 56px, var(--color-primary, #1b2450) 74px, var(--color-brand-grad-to, #7b6fd0) 100%)',
        color: 'var(--color-on-primary)',
        borderRadius: 16,
        padding: 20,
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 16,
        boxShadow: 'var(--shadow-soft)',
        boxSizing: 'border-box',
      }}
    >
      {/* 頂行：左上 logo（冇 logo 就回品牌名文字）＋ 右上相片（可選） */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        {logoUrl ? (
          <img src={logoUrl} alt={brandLabel} style={{ height: 34, display: 'block' }} />
        ) : (
          <span style={{ fontSize: 'var(--font-size-small, 1rem)', fontWeight: 700, letterSpacing: 0.5, color: 'var(--color-primary, #1b2450)' }}>
            {brandLabel}
          </span>
        )}
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            style={{
              width: 62,
              height: 62,
              borderRadius: '50%',
              objectFit: 'cover',
              display: 'block',
              border: '2px solid rgba(255,255,255,0.55)',
              background: 'rgba(255,255,255,0.15)',
              flex: '0 0 auto',
            }}
          />
        ) : null}
      </div>

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-title, 1.375rem)', fontWeight: 800, lineHeight: 1.2 }}>{name}</span>
          <span
            style={{
              fontSize: 'var(--font-size-tiny, 0.9375rem)',
              fontWeight: 700,
              padding: '2px 10px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.18)',
            }}
          >
            {statusLabel}
          </span>
        </div>
        <div style={{ fontSize: 'var(--font-size-small, 1rem)', opacity: 0.92, marginTop: 2 }}>{typeLabel}</div>
        {company ? <div style={{ fontSize: 'var(--font-size-tiny, 0.9375rem)', opacity: 0.8, marginTop: 2 }}>{company}</div> : null}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 'var(--font-size-tiny, 0.9375rem)', opacity: 0.8 }}>{memberNoLabel}</div>
          <div style={{ fontSize: 'var(--font-size-body, 1.125rem)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{memberNo ?? '—'}</div>
        </div>
        {/* ⚠️ 有效期**一定顯示**（未設 → `—`）：產品負責人 2026-10-06 指出「卡上要有到期日」 */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 'var(--font-size-tiny, 0.9375rem)', opacity: 0.8 }}>{expiresLabel}</div>
          <div style={{ fontSize: 'var(--font-size-body, 1.125rem)', fontWeight: 700 }}>{expiresText ?? '—'}</div>
        </div>
      </div>
    </article>
  );
}
